import React, { useEffect, useState } from 'react';
import {
  Search, ShieldCheck, Loader2, AlertTriangle, Plus, Pencil, Trash2, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../services/api';

const ROLES = [
  { value: 'investor', label: 'Investor' },
  { value: 'auction_house', label: 'Auction house' },
  { value: 'estate_agent', label: 'Estate agent' },
  { value: 'valuer', label: 'Valuer' },
  { value: 'deal_source', label: 'Deal source' },
  { value: 'admin', label: 'Admin' },
];

const STATUSES = [
  { value: 'trial', label: 'Trial' },
  { value: 'active', label: 'Active' },
  { value: 'past_due', label: 'Past due' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'none', label: 'None' },
];

const TIERS = [
  { value: 'none', label: 'None' },
  { value: 'investor', label: 'Investor' },
  { value: 'auction_house', label: 'Auction house' },
  { value: 'estate_agent', label: 'Estate agent' },
  { value: 'valuer', label: 'Valuer' },
  { value: 'deal_source', label: 'Deal source' },
  { value: 'white_label', label: 'White label' },
  { value: 'admin', label: 'Admin' },
];

const ACCESS_LEVELS = [
  { value: 'none', label: 'None' },
  { value: 'basic', label: 'Basic' },
  { value: 'pro', label: 'Pro' },
];

const TIER_COLOURS = {
  none: 'text-slate-500',
  investor: 'text-emerald-400',
  auction_house: 'text-amber-400',
  deal_source: 'text-purple-400',
  estate_agent: 'text-blue-400',
  valuer: 'text-cyan-400',
  white_label: 'text-pink-400',
  admin: 'text-rose-400',
};

const EMPTY_CREATE = {
  email: '',
  password: '',
  full_name: '',
  company_name: '',
  role: 'investor',
  is_verified: true,
  is_superuser: false,
  is_active: true,
  subscription_status: 'trial',
  subscription_tier: 'none',
  data_access_level: 'none',
  lifetime_free: false,
  is_broker: false,
};

function parseError(body, fallback) {
  if (!body?.detail) return fallback;
  if (typeof body.detail === 'string') return body.detail;
  if (Array.isArray(body.detail)) return body.detail.map(d => d.msg || d).join(', ');
  return fallback;
}

function UserFormModal({ mode, initial, onClose, onSaved, meId }) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState(initial);
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const set = (key) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [key]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name?.trim()) {
      toast.error('Full name is required');
      return;
    }
    if (!isEdit && (!form.email?.trim() || !form.password || form.password.length < 8)) {
      toast.error('Email and password (8+ chars) are required');
      return;
    }
    if (isEdit && password && password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        const payload = {
          full_name: form.full_name.trim(),
          company_name: form.company_name?.trim() || null,
          phone: form.phone?.trim() || null,
          role: form.role,
          is_active: form.is_active,
          is_superuser: form.is_superuser,
          is_verified: form.is_verified,
          lifetime_free: form.lifetime_free,
          is_broker: form.is_broker,
          subscription_status: form.subscription_status,
          subscription_tier: form.subscription_tier,
          data_access_level: form.data_access_level,
        };
        if (password) payload.password = password;

        const r = await authFetch(`/api/admin/users/${form.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(parseError(body, `HTTP ${r.status}`));
        }
        toast.success(`Updated ${form.email}`);
      } else {
        const r = await authFetch('/api/admin/users', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...form,
            email: form.email.trim(),
            full_name: form.full_name.trim(),
            company_name: form.company_name?.trim() || undefined,
          }),
        });
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(parseError(body, `HTTP ${r.status}`));
        }
        toast.success('User created');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const isMe = isEdit && form.id === meId;
  const cannotRevokeAdmin = isMe && form.is_superuser;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <h2 className="text-lg font-semibold text-white">
            {isEdit ? 'Edit user' : 'Create user'}
          </h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {!isEdit && (
            <>
              <Field label="Email" required>
                <input type="email" value={form.email} onChange={set('email')} required
                  className={inputCls} autoComplete="off" />
              </Field>
              <Field label="Password" required>
                <input type="password" value={form.password} onChange={set('password')} required minLength={8}
                  className={inputCls} autoComplete="new-password" />
              </Field>
            </>
          )}

          {isEdit && (
            <Field label="Email">
              <input type="text" value={form.email} disabled className={`${inputCls} opacity-60`} />
            </Field>
          )}

          <Field label="Full name" required>
            <input type="text" value={form.full_name} onChange={set('full_name')} required className={inputCls} />
          </Field>

          <Field label="Company">
            <input type="text" value={form.company_name || ''} onChange={set('company_name')} className={inputCls} />
          </Field>

          {isEdit && (
            <Field label="Phone">
              <input type="text" value={form.phone || ''} onChange={set('phone')} className={inputCls} />
            </Field>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Role">
              <select value={form.role} onChange={set('role')} className={inputCls}>
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </Field>
            <Field label="Access level">
              <select value={form.data_access_level} onChange={set('data_access_level')} className={inputCls}>
                {ACCESS_LEVELS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Subscription status">
              <select value={form.subscription_status} onChange={set('subscription_status')} className={inputCls}>
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Subscription tier">
              <select value={form.subscription_tier} onChange={set('subscription_tier')} className={inputCls}>
                {TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
          </div>

          {isEdit && (
            <Field label="New password (optional)">
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                minLength={8} placeholder="Leave blank to keep current" className={inputCls} />
            </Field>
          )}

          <div className="grid grid-cols-2 gap-2 pt-1">
            <Check label="Active" checked={form.is_active} onChange={set('is_active')} />
            <Check label="Email verified" checked={form.is_verified} onChange={set('is_verified')} />
            <Check
              label="Platform admin"
              checked={form.is_superuser}
              onChange={set('is_superuser')}
              disabled={cannotRevokeAdmin}
              title={cannotRevokeAdmin ? 'Cannot revoke your own admin access here' : ''}
            />
            <Check label="Lifetime free" checked={form.lifetime_free} onChange={set('lifetime_free')} />
            <Check label="Broker" checked={form.is_broker} onChange={set('is_broker')} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-slate-300 hover:text-white rounded-xl">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl disabled:opacity-50">
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create user'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls = 'w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 outline-none';

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">
        {label}{required && <span className="text-red-400"> *</span>}
      </label>
      {children}
    </div>
  );
}

function Check({ label, checked, onChange, disabled, title }) {
  return (
    <label title={title}
      className={`flex items-center gap-2 text-sm text-slate-300 cursor-pointer ${disabled ? 'opacity-50' : ''}`}>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled}
        className="w-4 h-4 accent-emerald-500" />
      {label}
    </label>
  );
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [onlyAdmins, setOnlyAdmins] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const [modal, setModal] = useState(null);

  const refresh = () => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (onlyAdmins) params.set('only_superusers', 'true');
    authFetch(`/api/admin/users?${params}`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(d => { setRows(d); setError(null); })
      .catch(e => setError(e.message));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [onlyAdmins]);

  const deleteUser = async (target) => {
    if (!window.confirm(`Delete ${target.email}? This cannot be undone.`)) return;
    setPendingId(target.id);
    try {
      const r = await authFetch(`/api/admin/users/${target.id}`, { method: 'DELETE' });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(parseError(body, `HTTP ${r.status}`));
      }
      toast.success(`Deleted ${target.email}`);
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ShieldCheck size={22} className="text-rose-400" /> Users
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Create, edit, assign roles, and manage platform access.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModal({ mode: 'create', data: { ...EMPTY_CREATE } })}
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold rounded-xl"
        >
          <Plus size={16} /> Create user
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[260px] relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && refresh()}
            placeholder="Search email, name, company..."
            className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-sm text-slate-200 placeholder-slate-500 focus:border-emerald-500 outline-none"
          />
        </div>
        <button type="button" onClick={() => refresh()}
          className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold rounded-xl">
          Search
        </button>
        <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
          <input type="checkbox" checked={onlyAdmins} onChange={e => setOnlyAdmins(e.target.checked)}
            className="w-4 h-4 accent-rose-500" />
          Admins only
        </label>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 text-sm">
          <AlertTriangle size={18} /> {error}
        </div>
      )}

      {!rows && !error && (
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      )}

      {rows && rows.length === 0 && (
        <p className="text-slate-500 text-sm">No users match those filters.</p>
      )}

      {rows && rows.length > 0 && (
        <div className="overflow-x-auto bg-slate-900 border border-slate-800 rounded-2xl">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-950/50 text-slate-400 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 font-medium">User</th>
                <th className="text-left px-4 py-3 font-medium">Role</th>
                <th className="text-left px-4 py-3 font-medium">Access</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.map(u => {
                const isMe = u.id === me?.id;
                const tierColour = TIER_COLOURS[u.subscription_tier] || 'text-slate-400';
                const busy = pendingId === u.id;
                return (
                  <tr key={u.id} className="hover:bg-slate-800/40">
                    <td className="px-4 py-3">
                      <div className="text-white font-medium flex items-center gap-2">
                        {u.full_name}
                        {u.is_superuser && (
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300">Admin</span>
                        )}
                        {!u.is_verified && (
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">Unverified</span>
                        )}
                      </div>
                      <div className="text-slate-500 text-xs">{u.email}{u.company_name ? ` · ${u.company_name}` : ''}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-300 capitalize">{u.role.replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3">
                      <span className={`capitalize ${tierColour}`}>{u.data_access_level}</span>
                      <span className="text-slate-600 text-xs ml-1">/ {u.subscription_tier.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-md text-xs ${u.is_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-800 text-slate-500'}`}>
                        {u.is_active ? u.subscription_status : 'inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => setModal({ mode: 'edit', data: { ...u } })}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-800 text-slate-300 hover:bg-slate-700"
                        >
                          <Pencil size={13} /> Edit
                        </button>
                        <button
                          type="button"
                          disabled={busy || isMe}
                          onClick={() => deleteUser(u)}
                          title={isMe ? 'Cannot delete your own account' : ''}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-red-500/10 text-red-300 hover:bg-red-500/20 disabled:opacity-40"
                        >
                          {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <UserFormModal
          mode={modal.mode}
          initial={modal.data}
          meId={me?.id}
          onClose={() => setModal(null)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}
